import { useEffect, useState } from 'react';
import { Box, Image } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { RefreshIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import OpenCamera from 'Component/open-camera';
import captureVideoFrame from 'Helper/captureVideoFrame';
import { useHostAttendanceRecordState } from 'View/host-attendance-record/state';
import selfieOverlay from 'Asset/image/selfie-overlay.svg';

import styles from './styles.module.css';
import RetryButton from 'Component/retry-button';
import ButtonSolid from 'UiKit/button/buttonSolid';

const RecordAttendance = ({ onPermissionDenied }) => {
  const { t } = useTranslation('host-attendance');
  const [isSnapping, setIsSnapping] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const { updateState, state } = useHostAttendanceRecordState();

  const isFirstCapture = state?.currentStep === 0;
  const defaultFacingMode = isFirstCapture ? 'user' : 'environment';
  const facingMode = isFlipped ? (defaultFacingMode === 'user' ? 'environment' : 'user') : defaultFacingMode;

  const isCaptureDisabled = !isCameraReady || state?.isPermissionDenied;

  const captureImage = () => {
    if (isCaptureDisabled) {
      if (state?.isPermissionDenied) onPermissionDenied?.(true);
      return;
    }
    setIsSnapping(true);
    setTimeout(() => setIsSnapping(false), 150);

    const capturedImage = captureVideoFrame({ videoElementId: 'video-stream', mirror: facingMode === 'user' });
    if (!capturedImage) return;

    updateState({ tempCapturedImage: capturedImage });
  };

  const handleNext = () => {
    updateState({
      capturedImage:
        state?.currentStep === 0
          ? { first: state?.tempCapturedImage, second: state?.capturedImage?.second }
          : { first: state?.capturedImage?.first, second: state?.tempCapturedImage },
      currentStep: state?.currentStep === 0 && state?.capturedImage?.second ? 2 : state?.currentStep + 1,
    });
    updateState({ tempCapturedImage: null });
  };

  useEffect(() => {
    setIsFlipped(false);
  }, [state?.currentStep]);

  return (
    <Box className={styles['record-attendance__container']}>
      <Box className={styles['record-attendance__top-section']}>
        <Condition statement={state?.tempCapturedImage}>
          <Image src={state?.tempCapturedImage} alt="captured-attendance" className={styles['record-attendance__preview-image']} />
        </Condition>
        <Condition statement={!state?.tempCapturedImage}>
          <Condition statement={isFirstCapture}>
            <Image src={selfieOverlay} alt="selfie-overlay" className={styles['record-attendance__selfie-overlay']} />
          </Condition>
          <Box className={styles['record-attendance__switch-button']} onClick={() => setIsFlipped((prev) => !prev)}>
            <RefreshIcon size="24px" color="var(--color-primary70)" />
          </Box>
          <OpenCamera
            isOpen={true}
            className={`${styles['camera__video']} ${facingMode === 'user' ? styles['camera__video--mirrored'] : ''}`}
            facingMode={facingMode}
            setIsCameraReady={setIsCameraReady}
            onPermissionDenied={onPermissionDenied}
          />
        </Condition>
      </Box>
      <Box className={styles['record-attendance__bottom-section']}>
        <Condition statement={!state?.tempCapturedImage}>
          <Box className={styles['record-attendance__instruction-text']}>
            {isFirstCapture ? t('capture_photo.selfie.instruction') : t('capture_photo.work_environment.instruction')}
          </Box>
          <Box
            className={`${styles['record-attendance__capture-button']} ${isCaptureDisabled ? styles['record-attendance__capture-button--disabled'] : ''}`}
            onClick={captureImage}
          />
        </Condition>
        <Condition statement={state?.tempCapturedImage}>
          <Box className={styles['record-attendance__instruction-text']} onClick={() => updateState({ tempCapturedImage: null })}>
            <RetryButton size="large" onClick={() => updateState({ tempCapturedImage: null })} />
          </Box>
          <ButtonSolid className={styles['record-attendance__next-button']} onClick={handleNext}>
            {t('common:next')}
          </ButtonSolid>
        </Condition>
      </Box>
      <Condition statement={isSnapping}>
        <Box className={styles['record-attendance__snap-effect']} />
      </Condition>
    </Box>
  );
};

export default RecordAttendance;
